<template>
  <div v-if="isOccurrenceFilledWithMandatoryData" class="so-updates" v-disable-component="occurrence.isCompleted || !userCanEdit">
    <div v-if="userCanEdit">
      <div class="label-bold mt-2 mb-1">Technical Occurrence Updates #{{ updates.length + 1 || 1 }}</div>
      <div class="row">
        <div class="col-12">
          <div>
            <div class="label mt-2 mb-1">Further steps</div>
            <textarea-autosize
              class="form-control comment-textarea"
              rows="2"
              placeholder="What are the next steps?"
              v-model="update.furtherSteps"
              :min-height="50"
            ></textarea-autosize>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-6">
          <div>
            <div class="label mt-2 mb-1 d-flex justify-content-between">
              <div>Part required for dispatch</div>

              <checkbox-with-label
                v-if="occurrence.materialRequestGuid"
                :label="'Include Material Request status'"
                v-model="update.includeMaterialRequestStatus"
              ></checkbox-with-label>
            </div>
            <textarea-autosize
              class="form-control comment-textarea"
              rows="2"
              placeholder="Enter part number(s) required for dispatch. (by filling out this field, the AOG desk will automatically receive this information by email.)"
              v-model="update.sparesStatus"
              :min-height="50"
            ></textarea-autosize>
          </div>
        </div>
        <div class="col-6">
          <div>
            <div class="label mt-2 mb-1">Tools status</div>
            <textarea-autosize
              class="form-control comment-textarea"
              rows="2"
              placeholder="Status of tools?"
              v-model="update.toolsStatus"
              :min-height="50"
            ></textarea-autosize>
          </div>
        </div>
      </div>

      <div>
        <div class="label mt-3 mb-1">Update text</div>
        <textarea-autosize
          class="form-control comment-textarea"
          rows="3"
          placeholder="Update text"
          v-model="update.text"
          :min-height="75"
        ></textarea-autosize>
      </div>

      <div>
        <div class="label mt-3 mb-1 d-flex justify-content-between">
          <div>A/C Change Delay</div>
        </div>

        <div class="row align-items-center">
          <div class="col-3 label">
            <checkbox-with-label :label="'Include total estimated delay'" v-model="update.includeEstimatedDelay" />
          </div>

          <div class="col-3" v-if="update.includeEstimatedDelay">
            <input type="number" class="form-control" v-model="update.estimatedDelayHours" placeholder="Delay Hours" />
          </div>
          <div v-if="update.includeEstimatedDelay">hrs</div>
        </div>
      </div>

      <div class="row mt-3" v-if="update.includeEstimatedDelay">
        <div class="col-12">
          <div>
            <textarea-autosize
              class="form-control comment-textarea"
              rows="2"
              placeholder="Delay estimation details"
              v-model="update.estimatedDelayDescription"
              :min-height="50"
            ></textarea-autosize>
          </div>
        </div>
      </div>

      <div class="row mt-2">
        <div class="col-4">
          <div class="row align-items-center">
            <div class="col-4 pt-1">
              <div class="label">Next Info</div>
            </div>
            <div class="col-8">
              <board-item-date-picker
                v-model="update.nextInfoDate"
                :min-date="new Date()"
                :display-quick-add-hours="true"
              ></board-item-date-picker>
            </div>
          </div>
        </div>
        <div class="col-8">
          <div class="row">
            <div class="col-1 pt-2">
              <div class="label">Notify</div>
            </div>

            <div class="col-11">
              <multiselect
                v-model="chosenRecipients"
                placeholder="Click to select, type to add recipient"
                label="name"
                track-by="name"
                :options="allReceiversList"
                :multiple="true"
                open-direction="bottom"
                :taggable="true"
                @tag="addRecipient"
                tagPlaceholder="Press enter to add the address"
              >
                <template slot="tag" slot-scope="props">
                  <div class="multiselect-tag">
                    <div :title="props.option.email" v-b-tooltip:hover>{{ props.option.name }}</div>
                    <i class="far fa-times cursor-pointer" @click="props.remove(props.option)"></i>
                  </div>
                </template>
              </multiselect>
            </div>
          </div>
        </div>
      </div>

      <div class="row mt-3 justify-content-end">
        <div class="col-2">
          <div
            v-b-tooltip.hover.auto
            :title="updateIsValid ? '' : 'Please specify update text, and estimated delay info, if the delay is included.'"
          >
            <button class="btn btn-block btn-success" :disabled="!updateIsValid || isSaving" @click="saveUpdate()">
              <div v-if="!isSaving">
                <i class="far fa-inbox-in"></i>
                Save update
              </div>
              <div v-else class="updates-loading d-flex align-items-center justify-content-center">
                <loading-spinner class="update-spinner" />
              </div>
            </button>
          </div>
        </div>
      </div>
      <hr />
    </div>
    <div class="previous-updates">
      <div class="label-bold mt-2 mb-4">Previous updates</div>

      <div v-if="updates.length == 0">No updates</div>

      <div v-for="upd in updates" :key="upd.id">
        <div class="container-fluid mb-2">
          <div class="row">
            <div class="avatar" v-b-tooltip.hover.auto :title="upd.user.username">
              {{ upd.user.tlc.substr(0, 3) }}
            </div>

            <div class="ml-1 col-6" style="margin-right: -10px">
              <div class="font-weight-bold">{{ upd.createdDate | formatDateTime }}</div>
              <div class="update-container">
                <table class="table table-sm update-next-Info">
                  <colgroup>
                    <col style="width: 150px" />
                  </colgroup>
                  <tr>
                    <td class="font-weight-bold text-nowrap">Further Steps</td>
                    <td class="multiline">{{ upd.furtherSteps }}</td>
                  </tr>

                  <tr>
                    <td class="font-weight-bold text-nowrap">Part required for dispatch</td>
                    <td class="multiline">{{ upd.sparesStatus }}</td>
                  </tr>

                  <tr>
                    <td class="font-weight-bold text-nowrap">Update Text</td>
                    <td class="multiline">{{ upd.text }}</td>
                  </tr>

                  <tr>
                    <td class="font-weight-bold text-nowrap">Tools Status</td>
                    <td class="multiline">{{ upd.toolsStatus }}</td>
                  </tr>

                  <tr>
                    <td class="font-weight-bold text-nowrap">Next Info</td>
                    <td>
                      <div v-if="upd.nextInfoDate">{{ upd.nextInfoDate | formatDateTime }}</div>
                      <div v-else>{{ ' No date set ' }}</div>
                    </td>
                  </tr>

                  <tr>
                    <td class="font-weight-bold text-nowrap">Estimated Delay Hours</td>
                    <td class="multiline">{{ upd.estimatedDelayHours || 'N/A' }}</td>
                  </tr>

                  <tr>
                    <td class="font-weight-bold text-nowrap">Estimated Delay Description</td>
                    <td class="multiline">{{ upd.estimatedDelayDescription || 'N/A' }}</td>
                  </tr>
                </table>

                <div class="container">
                  <div class="mt-2 row">
                    <div v-for="(r, index) in upd.recipients" :key="index">
                      <div class="badge badge-recipient mr-2" v-b-tooltip.hover.auto :title="r">
                        {{ getEmailName(r) }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div v-else class="alert alert-info d-flex justify-content-left align-items-center pt-4 pb-4">
    <i class="fa fa-exclamation-triangle pr-2"></i>
    <div>{{ publicConstants.fillMandatoryFields }}</div>
  </div>
</template>

<script lang="ts">
  import Vue from 'vue';
  import Component from 'vue-class-component';
  import { SpecialOccurrenceModule, SpecialOccurrenceStore } from '@/store/specialOccurrence';
  import TaskCard from '@/components/TaskCard.vue';
  import {
    OccurrenceUpdateRecipientDto,
    SpecialOccurrenceAccessLevel,
    SpecialOccurrenceUpdateDto,
    SpecialOccurrenceUpdatesDto,
    TechnicalOccurrenceDto,
  } from '@/apiclient/apiclient_generated';
  import LoadingSpinner from '@/components/Misc/LoadingSpinner.vue';
  import RouteNames from '@/types/routeNames';
  import PublicConstants from '@/types/publicConstants';

  @Component({
    components: {
      TaskCard,
      LoadingSpinner,
    },
  })
  export default class TechnicalOccurrenceUpdates extends Vue {
    isSaving = false;
    private store: SpecialOccurrenceModule = SpecialOccurrenceStore;
    update: SpecialOccurrenceUpdateDto = new SpecialOccurrenceUpdateDto();

    private manualRecipients: OccurrenceUpdateRecipientDto[] = [];
    private chosenRecipients: OccurrenceUpdateRecipientDto[] = [];

    publicConstants = PublicConstants;

    async mounted() {
      await this.store.loadSpecialOccurrenceUpdates(this.$route.params.occId);
      await this.store.loadOccurrenceUpdateRecipients();

      this.setDefaultRecipients();

      if (this.hasSOUpdateValues(this.store.soUpdateBackup)) {
        this.update = this.store.soUpdateBackup;
      }
    }

    get isOccurrenceFilledWithMandatoryData() {
      return (
        this.occurrence.name &&
        this.occurrence.technicalOccurrenceTypeId &&
        this.occurrence.aircraft &&
        this.occurrence.airport &&
        this.occurrence.groundTimeAta &&
        this.occurrence.scheduledTimeOfDeparture &&
        this.occurrence.generalRemarks
      );
    }

    private setDefaultRecipients() {
      Vue.set(this.update, 'recipients', []);

      const defaults = this.allReceiversList.filter((_) => _.isDefault);

      if (defaults.length) {
        const recipients: any = defaults.map((_) => {
          return {
            id: _.id,
            name: _.name,
            email: _.email,
          };
        });

        this.chosenRecipients = recipients;
      }
    }

    private hasSOUpdateValues(update: SpecialOccurrenceUpdateDto) {
      return update && Object.values(update).some((t) => t || t?.length);
    }

    beforeDestroy() {
      let updateBackupData = null;

      if (
        this.$route.name === RouteNames.specialOccurrence.root ||
        this.$route.name === RouteNames.specialOccurrence.attachments ||
        this.$route.name === RouteNames.specialOccurrence.taskGeneration ||
        this.$route.name === RouteNames.specialOccurrence.completion ||
        this.$route.name === RouteNames.specialOccurrence.accessLevels
      ) {
        updateBackupData = this.hasSOUpdateValues(this.update) ? this.update : null;
      }

      this.store.setSpecialOccurrenceUpdatesBackup(updateBackupData);
      this.store.setSpecialOccurrenceUpdates({} as SpecialOccurrenceUpdatesDto);
    }

    getEmailName(email) {
      return this.allReceiversList.find((_) => _.email === email)?.name || email.substr(0, email.indexOf('@'));
    }

    get updateIsValid() {
      return (
        this.update.text &&
        (this.update.includeEstimatedDelay ? this.update.estimatedDelayHours && this.update.estimatedDelayDescription : true)
      );
    }

    get allReceiversList() {
      return this.store.updateRecipients.concat(this.manualRecipients);
    }

    get occurrence(): TechnicalOccurrenceDto {
      return this.store.currentOccurrence;
    }

    get updates() {
      return this.lodash.orderBy(this.occurrence.specialOccurrenceUpdates, 'createdDate', 'desc');
    }

    get userCanEdit() {
      return (
        this.store.currentOccurrenceUserAccessLevel == SpecialOccurrenceAccessLevel.Admin ||
        this.store.currentOccurrenceUserAccessLevel == SpecialOccurrenceAccessLevel.Editor
      );
    }

    addRecipient(tag) {
      const recipient = new OccurrenceUpdateRecipientDto();
      recipient.email = tag;
      recipient.name = tag;
      this.manualRecipients.push(recipient);
      this.chosenRecipients.push(recipient);
    }

    async saveUpdate() {
      if (!this.updateIsValid || this.isSaving) {
        return;
      }

      this.isSaving = true;
      const recipients = this.chosenRecipients ? this.chosenRecipients.map((_) => _.email) : [];

      const updateObj = new SpecialOccurrenceUpdateDto(this.update);
      updateObj.specialOccurrenceId = this.$route.params.occId;
      updateObj.recipients = recipients;

      await this.store.createSpecialOccurrenceUpdate(updateObj);
      this.update = new SpecialOccurrenceUpdateDto();
      this.isSaving = false;

      this.setDefaultRecipients();
    }
  }
</script>

<style scoped lang="scss">
  .save-btn {
    padding-top: 0.2em;
  }

  .updates-loading {
    height: 22px;
  }

  .label-bold {
    font-weight: bold;
    font-size: 1.1rem;
  }

  .update-container {
    font-size: 1.1rem;
    border: solid 1px #ccc;
    margin: 5px;
    outline: none;

    box-shadow: 0 1px 0 rgba(9, 30, 66, 0.25);
    background: white;
    transition: all 0.25s ease-in-out;
    z-index: 1000;
    border-radius: 3px;
    padding: 5px 10px 5px 10px;

    .update-text {
      font-weight: bold;
      font-size: 0.8rem;
    }

    .update-next-Info {
      font-size: 0.8rem;
    }

    .badge-recipient {
      background: #357ebd;
      color: white;
      padding: 4px;
    }
  }

  .previous-updates {
    height: 45vh;
    padding: 0 4px;
    overflow-y: auto;
  }
  .previous-updates::-webkit-scrollbar {
    width: 8px;
  }

  .previous-updates::-webkit-scrollbar-track {
    background: #ddd;
    border-radius: 5px;
  }

  .previous-updates::-webkit-scrollbar-thumb {
    background: #999;
    border: 2px solid #ddd;
    border-radius: 5px;
  }

  .multiline {
    white-space: pre-line;
  }
</style>
